// =============================================================================
// identity-client.ts — Client API onboarding fournisseur, DOUBLABLE (Option B).
// =============================================================================
// « Option B confirmée : skeleton CI contre doubles » (GATE_HUMAN [S0]). Comme
// `ping-client.ts` / `parameters-client.ts`, ce module expose une INTERFACE
// (IdentityClient) miroir des endpoints supplier de D4 (apps/api/src/identity —
// SupplierActivation + MagicLink + SupplierSession) et une IMPLÉMENTATION stub
// déterministe. Le stub sera remplacé par l'impl HTTP réelle derrière la même
// interface, sans toucher l'UI.
//
// AUTORITÉ : l'API. Le FRONT ne fabrique JAMAIS de token, ne hashe rien, ne
// décide pas de l'expiration : il déclenche l'envoi (par email) et consomme le
// magic-link via son token. L'envoi email est STUBÉ (Postmark = S7, cf.
// research-S4 fork ii) : la landing reçoit un accusé « lien envoyé », jamais le
// lien en clair côté UI.
//
// Les DTO ci-dessous sont des miroirs HTTP (PAS le Published Language) : sur le
// fil, les dates sont des chaînes ISO8601 (sérialisation JSON), d'où `string`.

/** Issue de la demande de magic-link : accusé d'envoi (jamais le lien en clair). */
export interface MagicLinkRequestResult {
  /** Accusé : l'email a été pris en charge (envoi réel = Postmark S7, stubé ici). */
  readonly status: 'sent';
  /** Email destinataire (écho normalisé), pour l'affichage « lien envoyé à … ». */
  readonly email: string;
}

/**
 * Résultat de la consommation d'un magic-link (usage unique strict, IT-IDENTITY-02).
 * En succès, une SupplierSession opaque est ouverte côté API (cookie httpOnly) et le
 * tenant (SIRET) est résolu via GET /supplier/auth/whoami. Le FRONT n'en retient que
 * l'identité résolue : le routing CGU (signé / à signer) N'EST PLUS porté ici — il
 * passe par `legal.hasSigned()` en couche présentation (BC legal, PAS identity).
 */
export interface MagicLinkConsumeResult {
  /**
   * Tenant (SIRET) DÉRIVÉ de l'identité par l'API, résolu via la session ouverte
   * (GET /supplier/auth/whoami), JAMAIS du body (invariant n°1). Seule donnée
   * d'identité remontée : l'UI se câble sur le {siret} (subject non consommé, retiré).
   */
  readonly tenantId: string;
}

/**
 * Réponse de `GET /admin/auth/session` (sonde de session ADMIN, L-3). `subject` = identité OIDC
 * RÉELLE de l'admin (affichage seul, header) ; `role` constant 'AdminFull' (RBAC serveur). AUTORITÉ =
 * API : le FRONT ne fabrique jamais ce sujet. Miroir de AdminSessionController.session (403/401 sinon).
 */
export interface AdminSessionDto {
  readonly subject: string;
  readonly role: 'AdminFull';
  /** Nom d'affichage résolu (annuaire admin, DEC-47) ; null tant que l'IdP ne libère pas le profil. */
  readonly name: string | null;
}

/** Codes d'erreur métier renvoyés par l'API d'onboarding (UC-F-001 E1/E2). */
export type IdentityErrorCode =
  | 'TOKEN_EXPIRED'
  | 'TOKEN_CONSUMED'
  | 'TOKEN_INVALID'
  | 'RATE_LIMITED'
  | 'UNKNOWN';

/**
 * Erreur applicative renvoyée par l'API (HTTP 4xx). L'UI l'affiche telle quelle
 * et adapte l'action de remédiation au `code` (renvoi de lien, reconnexion).
 * Elle ne réinterprète JAMAIS la règle métier : elle relaie le message serveur.
 */
export class IdentityApiError extends Error {
  public readonly code: IdentityErrorCode;

  public constructor(code: IdentityErrorCode, message: string) {
    super(message);
    this.name = 'IdentityApiError';
    this.code = code;
  }
}

/** Contrat du client onboarding — miroir des endpoints supplier de D4. */
export interface IdentityClient {
  /**
   * POST /supplier/auth/activate — ACTIVATION INITIALE du credential fournisseur
   * (S0-ACTIVATION, F1/F2). Consomme l'ActivationToken (secret du lien d'activation,
   * usage unique strict), active le credential ET ouvre la SupplierSession (l'API pose
   * les cookies `supplier_session` + `supplier_csrf`). Réponse 200 `{ activated: true }` :
   * on n'inspecte pas le corps, la session ouverte suffit — l'UI enchaîne vers la
   * confirmation des coordonnées puis la CGU. Lève IdentityApiError (401) si le token est
   * absent/invalide/expiré/déjà consommé. DISTINCT de `consumeMagicLink` (reconnexion).
   */
  activate(token: string): Promise<void>;
  /**
   * POST /supplier/auth/magic-link — demande un magic-link (l'email saisi sert
   * d'identifiant de credential, `{ credentialId }`). Réponse 202 CONSTANTE
   * (anti-énumération) : accusé « lien envoyé ». L'API émet `DE.MagicLinkIssued`
   * et déclenche l'envoi (Postmark S7). Le plafond anti-abus 3/h/credential est
   * ABSORBÉ par le 202 constant côté serveur (jamais observable ici).
   */
  requestMagicLink(email: string): Promise<MagicLinkRequestResult>;
  /**
   * Consomme un magic-link (usage unique strict) : POST /supplier/auth/session
   * `{ token }` ouvre la SupplierSession opaque (cookie httpOnly), PUIS
   * GET /supplier/auth/whoami résout le tenant (siret). Le routing CGU N'EST PLUS
   * ici (il passe par `legal.hasSigned()` en présentation). Lève IdentityApiError
   * si le lien est expiré/consommé/invalide.
   */
  consumeMagicLink(token: string): Promise<MagicLinkConsumeResult>;
  /**
   * GET /admin/auth/session — sonde la session ADMIN (cookie httpOnly, LECTURE → pas de CSRF). En
   * succès renvoie `{ subject, role:'AdminFull' }` (identité RÉELLE, affichage seul, L-3) ; lève
   * IdentityApiError si aucune session admin (401/403). Consommé par le bootstrap dynamique
   * (résolution 3-voies : supplier → admin → landing). Appelé HORS TanStack Query (pas d'intercepteur).
   */
  getAdminSession(): Promise<AdminSessionDto>;
  /**
   * POST /admin/auth/logout — révoque la session ADMIN côté SERVEUR (transition d'état
   * de `admin_session`, l'autorité de révocation est l'API) ET efface les cookies
   * (`admin_session` httpOnly + `admin_csrf`). Protégé par CSRF double-submit : l'en-tête
   * `x-csrf-token` reprend la valeur du cookie NON-httpOnly `admin_csrf` (lu via document.cookie).
   * Idempotent (204, corps vide) : un 2ᵉ appel sans session est un no-op propre. Best-effort côté
   * client — l'appelant navigue vers la landing quoi qu'il advienne (purge de l'état d'auth client).
   */
  adminLogout(): Promise<void>;
}
